import { useCallback } from 'react';
import type { ReactNode } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import type { TableColumnType } from '../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../grid/reduxStore/table.types';
import { isRowBelongsToTotal } from '../../../../../muiTheme/dataGridUtils';
import { NUMBER_COLUMNS_ALLOWING_PRICING } from './variables';
import CellInputs from './CellInput';
import type { GridCellParams, GridColumnHeaderParams } from '@mui/x-data-grid-pro';
import { CustomTableCell } from './components/CustomTableCell';
import { DiscountSubtotalCell } from './components/DiscountSubtotalCell';
import { TableNumericCell } from './tableUtils/customCellHelpers';
import { isNumericColumn, IsPricingCellWithRounding } from './helper';

import { TipTapRenderEditCell } from '../../TiptapEditor/components/TipTapRenderEditCell';
import { TipTapRenderCell } from '../../TiptapEditor/components/TipTapRenderCell';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../utils/featureFlags.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { selectActiveCurrencySymbol } from '../../../grid/reduxStore/documentSettingsSlice.ts';
import { DOLLAR_SIGN } from './defaultData.tsx';
import { ColumnResizeHandle } from './ColumnResizeHandle.tsx';
import { TableContextMenuButton } from './components/TableContextMenuButton.tsx';
import { isGeneratedRow } from '../../../grid/reduxStore/crmRowMarkers';

export const isEligibleForStyling = (rowType: RowTypesIdentifier, columnType: ColumnTypeIdentifier): boolean => {
  return (
    [RowTypesIdentifier.FOOTER, RowTypesIdentifier.HEADER].includes(rowType) ||
    (rowType === RowTypesIdentifier.BODY && columnType === ColumnTypeIdentifier.TEXT)
  );
};

interface CellContext {
  sectionId: string;
  blockId: string;
  pricingDecimalPlaces: number;
  documentCurrencySymbol: string;
  activeHoverColumn: boolean;
  leftColumnId: string | null;
  columnType: ColumnTypeIdentifier;
}

const renderPricingCellContent = (params: GridCellParams, ctx: CellContext) => (
  <TableNumericCell
    documentCurrencySymbol={ctx.documentCurrencySymbol}
    params={params}
    columnType={ctx.columnType}
    numberOfDecimalPlaces={ctx.pricingDecimalPlaces}
  />
);

const renderNumericCellContent = (params: GridCellParams, ctx: CellContext) => (
  <TableNumericCell documentCurrencySymbol={ctx.documentCurrencySymbol} params={params} columnType={ctx.columnType} />
);

const renderTipTapViewCellContent = (params: GridCellParams, ctx: CellContext) => (
  <TipTapRenderCell
    id={params.id}
    field={params.field}
    columnType={ctx.columnType}
    rowType={params.row.rowType}
    blockId={ctx.blockId}
    sectionId={ctx.sectionId}
    numberOfDecimalPlaces={ctx.pricingDecimalPlaces}
    hasFocus={params.hasFocus}
  />
);

const renderTipTapEditCellContent = (params: GridCellParams, ctx: CellContext) => (
  <TipTapRenderEditCell
    field={params.field}
    hasFocus={params.hasFocus}
    id={params.id}
    columnType={ctx.columnType}
    rowType={params.row.rowType}
  />
);

const renderDefaultViewCellContent = (params: GridCellParams) => <CustomTableCell params={params} value={params?.value as string} />;

const renderDefaultEditCellContent = (params: GridCellParams, ctx: CellContext) => (
  <CellInputs params={params} blockId={ctx.blockId} sectionId={ctx.sectionId} currencySymbol={ctx.documentCurrencySymbol} />
);

const getViewCellContent = (params: GridCellParams, ctx: CellContext): JSX.Element => {
  const isStylingApplicable = isEligibleForStyling(params.row.rowType, ctx.columnType);
  const isOptional = ctx.columnType === ColumnTypeIdentifier.OPTIONAL;

  if (ctx.columnType === ColumnTypeIdentifier.SUBTOTAL && params.row.rowType === RowTypesIdentifier.BODY) {
    return (
      <DiscountSubtotalCell
        params={params}
        numberOfDecimalPlaces={ctx.pricingDecimalPlaces}
        documentCurrencySymbol={ctx.documentCurrencySymbol}
      />
    );
  }

  if (IsPricingCellWithRounding(ctx.columnType, params.row.rowType)) return renderPricingCellContent(params, ctx);
  if (isNumericColumn(ctx.columnType) && !isStylingApplicable) return renderNumericCellContent(params, ctx);
  if (isStylingApplicable && !isOptional) return renderTipTapViewCellContent(params, ctx);

  return renderDefaultViewCellContent(params);
};

const getEditCellContent = (params: GridCellParams, ctx: CellContext): JSX.Element => {
  const isStylingApplicable = isEligibleForStyling(params.row.rowType, ctx.columnType);
  const isOptional = ctx.columnType === ColumnTypeIdentifier.OPTIONAL;

  if (isStylingApplicable && !isOptional) return renderTipTapEditCellContent(params, ctx);

  return renderDefaultEditCellContent(params, ctx);
};

const renderResizeHandle = (params: GridCellParams, ctx: CellContext) => (
  <ColumnResizeHandle leftColumnId={ctx.leftColumnId} columnId={params.field} isActive={ctx.activeHoverColumn} />
);

const renderViewCell = (
  params: GridCellParams,
  ctx: CellContext,
  onOpen: (anchorEl: HTMLElement) => void,
  onRowMenuOpen: (params: GridCellParams) => void
) => {
  return (
    <>
      {ctx.leftColumnId !== 'id' && renderResizeHandle(params, ctx)}
      {getViewCellContent(params, ctx)}
      {!isGeneratedRow(params.row) && (
        <TableContextMenuButton
          onOpen={(anchorEl) => {
            onRowMenuOpen(params);
            onOpen(anchorEl);
          }}
        />
      )}
    </>
  );
};

const renderEditCell = (
  params: GridCellParams,
  ctx: CellContext,
  onOpen: (anchorEl: HTMLElement) => void,
  onRowMenuOpen: (params: GridCellParams) => void
) => (
  <>
    {ctx.leftColumnId !== 'id' && renderResizeHandle(params, ctx)}
    {getEditCellContent(params, ctx)}
    {!isGeneratedRow(params.row) && (
      <TableContextMenuButton
        onOpen={(anchorEl) => {
          onRowMenuOpen(params);
          onOpen(anchorEl);
        }}
      />
    )}
  </>
);

const COLUMN_TYPE_ICONS: Partial<Record<ColumnTypeIdentifier, ReactNode>> = {
  [ColumnTypeIdentifier.TEXT]: <TextFieldsIcon style={{ fontSize: 'inherit', opacity: 0.7 }} />,
  [ColumnTypeIdentifier.PRICE]: <AttachMoneyIcon style={{ fontSize: 'inherit', opacity: 0.7 }} />,
  [ColumnTypeIdentifier.MULTIPLIER]: <CloseIcon style={{ fontSize: 'inherit', opacity: 0.7 }} />,
  [ColumnTypeIdentifier.SUBTOTAL]: <AttachMoneyIcon style={{ fontSize: 'inherit', opacity: 0.7 }} />,
};

const renderHeaderCell = (params: GridColumnHeaderParams, ctx: CellContext) => {
  const icon = COLUMN_TYPE_ICONS[ctx.columnType];

  return (
    <>
      <ColumnResizeHandle leftColumnId={ctx.leftColumnId} columnId={params.field} isActive={ctx.activeHoverColumn} />
      {icon}
      {ctx.columnType !== ColumnTypeIdentifier.OPTIONAL && <span>{ctx.columnType}</span>}
    </>
  );
};

export const applyColSpanRules = (columns: TableColumnType[]): TableColumnType[] => {
  const firstDataColumnIndex = 1;
  if (columns.length <= firstDataColumnIndex) return columns;

  const textColumn = columns.find((col) => col.columnType === ColumnTypeIdentifier.TEXT);
  if (!textColumn) return columns;

  const colSpan = (_value, row): number => {
    if (row.rowType === RowTypesIdentifier.FOOTER) return columns.length - 1;
    if (isRowBelongsToTotal(row)) {
      return columns.length > NUMBER_COLUMNS_ALLOWING_PRICING ? columns.length - NUMBER_COLUMNS_ALLOWING_PRICING : 1;
    }
    return 1;
  };

  const isFirstColumnOptional = columns[firstDataColumnIndex].columnType === ColumnTypeIdentifier.OPTIONAL;

  if (isFirstColumnOptional) {
    const originalRenderCell = columns[firstDataColumnIndex].renderCell;
    const originalRenderEditCell = columns[firstDataColumnIndex].renderEditCell;
    columns[firstDataColumnIndex] = {
      ...columns[firstDataColumnIndex],
      colSpan,
      renderCell: (params) => {
        if (params.row.rowType === RowTypesIdentifier.FOOTER) {
          return textColumn.renderCell?.({ ...params, field: textColumn.field, value: params.row[textColumn.field] });
        }
        return originalRenderCell?.(params) ?? null;
      },
      renderEditCell: (params) => {
        if (params.row.rowType === RowTypesIdentifier.FOOTER) {
          return textColumn.renderEditCell?.({ ...params, field: textColumn.field, value: params.row[textColumn.field] });
        }
        return originalRenderEditCell?.(params) ?? null;
      },
    };
  } else {
    columns[firstDataColumnIndex] = {
      ...columns[firstDataColumnIndex],
      colSpan,
    };
  }

  return columns;
};

export function useApplyCustomRulesToColumns(
  sectionId: string,
  blockId: string,
  pricingDecimalPlaces: number,
  onMenuOpen: (anchorEl: HTMLElement) => void,
  onRowMenuOpen: (params: GridCellParams) => void
) {
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];
  const activeCurrencySymbol = useAppSelector(selectActiveCurrencySymbol) || DOLLAR_SIGN;
  const documentCurrencySymbol = isCurrencySettingsEnabled ? activeCurrencySymbol : DOLLAR_SIGN;

  const applyCustomRendering = useCallback(
    (columns: TableColumnType[]): TableColumnType[] => {
      return columns.map((column, index) => {
        const ctx: CellContext = {
          sectionId,
          blockId,
          pricingDecimalPlaces,
          documentCurrencySymbol,
          activeHoverColumn: column.activeHoverColumn || false,
          leftColumnId: index >= 1 ? columns[index - 1]?.field ?? null : null,
          columnType: column.columnType,
        };

        const { type, ...rest } = column;

        return {
          ...rest,
          type,
          headerAlign: isNumericColumn(column.columnType) ? 'right' : 'left',
          align: isNumericColumn(column.columnType) ? 'right' : 'left',
          headerClassName: column.columnSelected ? 'py-column-selected' : '',
          renderCell: (params: GridCellParams) => renderViewCell(params, ctx, onMenuOpen, onRowMenuOpen),
          renderEditCell: (params: GridCellParams) => renderEditCell(params, ctx, onMenuOpen, onRowMenuOpen),
          renderHeader: (params: GridColumnHeaderParams) => renderHeaderCell(params, ctx),
        } as TableColumnType;
      });
    },
    [sectionId, blockId, pricingDecimalPlaces, documentCurrencySymbol, onMenuOpen]
  );

  const applyCustomRulesToColumns = useCallback(
    (columns: TableColumnType[]): TableColumnType[] => applyColSpanRules(applyCustomRendering(columns)),
    [applyCustomRendering]
  );

  return { applyCustomRulesToColumns };
}
